/**
 * Single Elimination Tournament Component
 *
 * 싱글 엘리미네이션 토너먼트 브라켓을 렌더링하는 컴포넌트
 */

import React from 'react';
import '@assets/css/comp-form-trmntBracket.css';
import '@assets/css/competition.css';

const CompSingleElimination = ({
    tournamentData,
    theme = 'light',
    onPlayerClick,
    isFromHome = false
}) => {
    /**
     * 시드를 타입별로 그룹화
     */
    const groupBySeedType = () => {
        const grouped = {
            winner: [],
            loser: [],
            loserFinal: [],
            grandFinal: []
        };

        tournamentData.forEach(seed => {
            if (seed.seedType === 'W') {
                grouped.winner.push(seed);
            } else if (seed.seedType === 'L') {
                grouped.loser.push(seed);
            } else if (seed.seedType === 'LF') {
                grouped.loserFinal.push(seed);
            } else if (seed.seedType === 'GF') {
                grouped.grandFinal.push(seed);
            }
        });

        return grouped;
    };

    /**
     * 승자조를 라운드별로 그룹화
     */
    const groupWinnerByRound = (matches) => {
        if (!matches || matches.length === 0) return [];

        const sortedMatches = [...matches].sort((a, b) => a.seedNo - b.seedNo);
        const rounds = [];
        let currentRoundSize = Math.ceil(sortedMatches.length / 2);
        let matchIndex = 0;

        while (matchIndex < sortedMatches.length) {
            const round = [];
            const takeCount = Math.min(currentRoundSize, sortedMatches.length - matchIndex);

            for (let i = 0; i < takeCount; i++) {
                round.push(sortedMatches[matchIndex++]);
            }

            rounds.push(round);
            currentRoundSize = Math.ceil(currentRoundSize / 2);
        }

        return rounds;
    };

    /**
     * 매치 렌더링
     */
    const renderMatch = (seed, roundIndex) => {
        const isPlayerAWinner = seed.winPlyr === seed.plyrCdA;
        const isPlayerBWinner = seed.winPlyr === seed.plyrCdB;
        const hasWinner = seed.winPlyr !== null && seed.winPlyr !== undefined;
        const canSelect = seed.plyrCdA || seed.plyrCdB;
        const isClickable = canSelect;

        return (
            <div
                key={`${seed.seedType}-${seed.seedNo}`}
                className={`bracket-match ${hasWinner ? 'completed' : ''} ${isClickable ? 'selectable' : ''}`}
                data-seed={seed.seedNo}
                data-round={roundIndex}
            >
                <div className="match-number" style={{color: theme === 'dark' ? '#a0a0a0' : '#6c757d'}}>
                    <span style={{ fontSize: '15px' }}>#{seed.seedNo}</span>
                    {!seed.plyrCdA && !seed.plyrCdB && (
                        <div className="match-waiting">
                            <small style={{ fontSize: '9px' }}>대기 중</small>
                        </div>
                    )}
                </div>

                <div className="match-players-container">
                    <div
                        className={`match-player ${isPlayerAWinner ? 'winner' : ''} ${!seed.plyrCdA || seed.plyrCdA === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && onPlayerClick(seed, 'A')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmA || 'BYE'}
                        </span>
                        {isPlayerAWinner && <span className="winner-icon">👑</span>}
                    </div>

                    <div
                        className={`match-player ${isPlayerBWinner ? 'winner' : ''} ${!seed.plyrCdB || seed.plyrCdB === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && onPlayerClick(seed, 'B')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmB || 'BYE'}
                        </span>
                        {isPlayerBWinner && <span className="winner-icon">👑</span>}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * 싱글 엘리미네이션 렌더링
     */
    const renderSingleElimination = () => {
        const grouped = groupBySeedType();
        const winnerRounds = groupWinnerByRound(grouped.winner);

        return (
            <div className={`tournament-bracket-wrapper ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
                <div className="section-header bg-primary text-white p-2 rounded">
                    <h5 className="mb-0">🏆 싱글 엘리미네이션</h5>
                </div>

                <div className="bracket-rounds-container">
                    {winnerRounds.map((round, roundIndex) => (
                        <div key={`round-${roundIndex}`} className="bracket-round">
                            <div className="round-title">
                                <span className="badge bg-primary">
                                    {roundIndex === winnerRounds.length - 1 ? '결승' : `Round ${roundIndex + 1}`}
                                </span>
                            </div>
                            <div className="round-matches">
                                {round.map(seed => renderMatch(seed, roundIndex))}
                            </div>
                            {roundIndex < winnerRounds.length - 1 && (
                                <div className="round-connector">
                                    <svg className="connector-lines" width="100%" height="100%">
                                        {round.map((seed, idx) => {
                                            if (idx % 2 === 0 && idx + 1 < round.length) {
                                                return (
                                                    <g key={`connector-${idx}`}>
                                                        <line x1="0" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                              x2="50%" y2={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                        <line x1="0" y1={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                              x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                        <line x1="50%" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                              x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                        <line x1="50%"
                                                              y1={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                              x2="100%"
                                                              y2={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                              stroke="#0d6efd" strokeWidth="2"/>
                                                    </g>
                                                );
                                            }
                                            return null;
                                        })}
                                    </svg>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return renderSingleElimination();
};

export default CompSingleElimination;
